import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import InviteBotModal from './InviteBotModal';
import AiRecapModal from './AiRecapModal';
import type { Meeting, BotStatus } from '../../types';

const BOT_STATUS_CONFIG: Record<BotStatus, { label: string; color: string; bg: string; icon: string; pulse?: boolean }> = {
  idle:         { label: 'No Bot',       color: '#718096', bg: '#F1F4F8',   icon: 'ti-robot-off'    },
  scheduled:    { label: 'Scheduled',    color: '#6366F1', bg: '#EEF2FF',   icon: 'ti-clock'        },
  joining:      { label: 'Joining…',     color: '#C8973A', bg: '#FFFBEB',   icon: 'ti-loader-2', pulse: true },
  recording:    { label: 'Recording',    color: '#EF4444', bg: '#FFF5F5',   icon: 'ti-record-mail', pulse: true },
  transcribing: { label: 'Transcribing', color: '#6366F1', bg: '#EEF2FF',   icon: 'ti-loader-2', pulse: true },
  summarising:  { label: 'Summarising',  color: '#8B5CF6', bg: '#F5F3FF',   icon: 'ti-sparkles', pulse: true },
  done:         { label: 'Recap Ready',  color: '#22C55E', bg: '#F0FFF4',   icon: 'ti-circle-check' },
  error:        { label: 'Error',        color: '#EF4444', bg: '#FFF5F5',   icon: 'ti-circle-x'    },
  cancelled:    { label: 'Cancelled',    color: '#718096', bg: '#F1F4F8',   icon: 'ti-ban'          },
};

const PLATFORM_CONFIG: Record<string, { icon: string; label: string; color: string }> = {
  gmeet: { icon: 'ti-brand-google', label: 'Google Meet', color: '#1A73E8' },
  zoom:  { icon: 'ti-video',        label: 'Zoom',        color: '#2D8CFF' },
  teams: { icon: 'ti-brand-teams',  label: 'Teams',       color: '#5059C9' },
  other: { icon: 'ti-link',         label: 'Meeting',     color: '#718096' },
};

export default function MeetingsScreen() {
  const { meetings, meetingAiNotes, botServerOnline, cancelBot, toast } = useApp();
  const { isMobile, rp } = useBreakpoint();
  const [inviteTarget, setInviteTarget] = useState<Meeting | null>(null);
  const [recapTarget, setRecapTarget] = useState<Meeting | null>(null);
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'done'>('all');

  const px = rp({ mobile: '14px', tablet: '18px', desktop: '24px' });

  const filtered = meetings.filter(m => {
    if (filter === 'upcoming') return m.botStatus !== 'done';
    if (filter === 'done') return m.botStatus === 'done';
    return true;
  });

  const recapCount = meetings.filter(m => m.hasRecap).length;
  const activeCount = meetings.filter(m => ['joining', 'recording', 'transcribing', 'summarising'].includes(m.botStatus)).length;

  function handleCancelBot(m: Meeting) {
    cancelBot(m.id);
    toast('Bot cancelled', 'info');
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: `14px ${px}`, borderBottom: '1px solid var(--border)', flexShrink: 0, background: 'white' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className="ti ti-calendar-event" style={{ fontSize: '18px', color: '#6366F1' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: rp({ mobile: '16px', desktop: '20px' }), fontWeight: '800', color: 'var(--navy)' }}>Meetings</div>
          {!isMobile && <div style={{ fontSize: '12px', color: 'var(--text3)' }}>AI Notetaker — auto-join, transcribe & recap</div>}
        </div>
        {!isMobile && (
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            {[
              { label: 'Total',    value: meetings.length, color: 'var(--navy)' },
              { label: 'Active',   value: activeCount,     color: '#EF4444'     },
              { label: 'Recaps',   value: recapCount,      color: '#22C55E'     },
            ].map(s => (
              <div key={s.label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: '800', color: s.color }}>{s.value}</div>
                <div style={{ fontSize: '10px', color: 'var(--text3)' }}>{s.label}</div>
              </div>
            ))}
            {/* Bot server indicator */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 10px', borderRadius: '20px', background: botServerOnline ? '#F0FFF4' : '#FFF5F5', border: `1px solid ${botServerOnline ? '#9AE6B4' : '#FEB2B2'}` }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: botServerOnline ? '#22C55E' : '#EF4444' }} />
              <span style={{ fontSize: '10px', fontWeight: '700', color: botServerOnline ? '#276749' : '#C53030' }}>
                {botServerOnline ? 'Bot Online' : 'Bot Offline'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Active bot banner */}
      {activeCount > 0 && (
        <div style={{ padding: `8px ${px}`, background: '#FFF5F5', borderBottom: '1px solid #FEB2B2', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#EF4444', animation: 'pulse 1.5s infinite' }} />
            <span style={{ fontSize: '12px', color: '#C53030', fontWeight: '600' }}>
              {activeCount} bot{activeCount > 1 ? 's' : ''} currently active
            </span>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: '#F8FAFC', flexShrink: 0, padding: `0 ${px}` }}>
        {(['all', 'upcoming', 'done'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{ padding: '10px 14px', border: 'none', background: 'none', fontFamily: 'inherit', fontSize: '12px', fontWeight: '600', cursor: 'pointer', color: filter === f ? '#0F1B2D' : '#718096', borderBottom: filter === f ? '2px solid #6366F1' : '2px solid transparent', textTransform: 'capitalize' }}
          >
            {f === 'all' ? `All (${meetings.length})` : f === 'done' ? `Recaps (${recapCount})` : 'Upcoming'}
          </button>
        ))}
      </div>

      {/* Cards */}
      <div style={{ flex: 1, overflowY: 'auto', padding: `16px ${px}` }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text3)' }}>
            <i className="ti ti-calendar-off" style={{ fontSize: '40px', display: 'block', marginBottom: '10px', color: '#CBD5E0' }} />
            <div style={{ fontSize: '14px', fontWeight: '600' }}>No meetings found</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: rp({ mobile: '1fr', tablet: '1fr 1fr', desktop: 'repeat(auto-fill,minmax(320px,1fr))' }), gap: '14px', alignContent: 'start' }}>
            {filtered.map(meeting => {
              const status = BOT_STATUS_CONFIG[meeting.botStatus] || BOT_STATUS_CONFIG.idle;
              const platform = PLATFORM_CONFIG[meeting.platform] || PLATFORM_CONFIG.other;
              const isActive = ['joining', 'recording', 'transcribing', 'summarising'].includes(meeting.botStatus);
              const canInvite = ['idle', 'error', 'cancelled'].includes(meeting.botStatus);
              const recap = meetingAiNotes[meeting.id];

              return (
                <div
                  key={meeting.id}
                  style={{ background: 'white', border: `1.5px solid ${isActive ? '#FEB2B2' : 'var(--border)'}`, borderRadius: '14px', overflow: 'hidden', transition: 'all .18s', boxShadow: isActive ? '0 0 0 3px rgba(239,68,68,.1)' : undefined }}
                  onMouseEnter={e => { if (!isActive) { e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,.09)'; e.currentTarget.style.transform = 'translateY(-2px)'; } }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = isActive ? '0 0 0 3px rgba(239,68,68,.1)' : ''; e.currentTarget.style.transform = ''; }}
                >
                  {/* Card header */}
                  <div style={{ padding: '14px 16px', background: 'linear-gradient(135deg, #0F1B2D, #1A2D45)', position: 'relative' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '6px' }}>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: 'white', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{meeting.title}</div>
                      {/* Bot status badge */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '20px', background: status.bg, flexShrink: 0 }}>
                        <i className={`ti ${status.icon}`} style={{ fontSize: '10px', color: status.color, ...(status.pulse ? { animation: 'spin 1.2s linear infinite' } : {}) }} />
                        <span style={{ fontSize: '9px', fontWeight: '700', color: status.color, whiteSpace: 'nowrap' }}>{status.label}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <i className={`ti ${platform.icon}`} style={{ fontSize: '12px', color: 'rgba(255,255,255,.6)' }} />
                        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,.6)' }}>{platform.label}</span>
                      </div>
                      <span style={{ fontSize: '11px', color: 'rgba(255,255,255,.4)' }}>·</span>
                      <span style={{ fontSize: '11px', color: 'rgba(255,255,255,.6)' }}>{meeting.date} {meeting.time}</span>
                      {meeting.duration && (
                        <>
                          <span style={{ fontSize: '11px', color: 'rgba(255,255,255,.4)' }}>·</span>
                          <span style={{ fontSize: '11px', color: 'rgba(255,255,255,.6)' }}>{meeting.duration}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div style={{ padding: '12px 16px' }}>
                    {/* Participants */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                      {meeting.participants.map((p, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'var(--navy)', color: 'var(--gold)', fontSize: '8px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{p.ini}</div>
                          <div>
                            <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--navy)', lineHeight: 1.2 }}>{p.name}</div>
                            {p.org && <div style={{ fontSize: '9px', color: 'var(--text3)' }}>{p.org}</div>}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Progress bar for active states */}
                    {isActive && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '10px', color: '#718096', marginBottom: '4px', fontWeight: '600' }}>
                          {meeting.botStatus === 'joining' && 'Bot joining the call…'}
                          {meeting.botStatus === 'recording' && 'Recording in progress…'}
                          {meeting.botStatus === 'transcribing' && 'Transcribing audio…'}
                          {meeting.botStatus === 'summarising' && 'Generating AI recap…'}
                        </div>
                        <div style={{ height: '4px', borderRadius: '2px', background: '#E2E8F0', overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', borderRadius: '2px',
                            background: '#6366F1',
                            width: meeting.botStatus === 'joining' ? '25%' : meeting.botStatus === 'recording' ? '50%' : meeting.botStatus === 'transcribing' ? '75%' : '90%',
                            transition: 'width .5s ease',
                          }} />
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {canInvite && (
                        <button
                          onClick={() => setInviteTarget(meeting)}
                          style={{ flex: 2, padding: '8px', borderRadius: '8px', background: '#6366F1', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '11px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                        >
                          <i className="ti ti-robot" /> Invite Bot
                        </button>
                      )}

                      {meeting.botStatus === 'scheduled' && (
                        <button
                          onClick={() => handleCancelBot(meeting)}
                          style={{ flex: 2, padding: '8px', borderRadius: '8px', background: '#FFF5F5', border: '1.5px solid #FEB2B2', color: '#C53030', fontFamily: 'inherit', fontSize: '11px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                        >
                          <i className="ti ti-x" /> Cancel Bot
                        </button>
                      )}

                      {isActive && (
                        <button
                          onClick={() => handleCancelBot(meeting)}
                          style={{ flex: 1, padding: '8px', borderRadius: '8px', background: '#FFF5F5', border: '1.5px solid #FEB2B2', color: '#C53030', fontFamily: 'inherit', fontSize: '11px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                        >
                          <i className="ti ti-square-x" /> Stop
                        </button>
                      )}

                      {meeting.hasRecap && recap && (
                        <button
                          onClick={() => setRecapTarget(meeting)}
                          style={{ flex: 2, padding: '8px', borderRadius: '8px', background: '#F0FFF4', border: '1.5px solid #9AE6B4', color: '#276749', fontFamily: 'inherit', fontSize: '11px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                        >
                          <i className="ti ti-sparkles" /> View Recap
                        </button>
                      )}

                      <button
                        onClick={() => { if (meeting.meetUrl) window.open(meeting.meetUrl, '_blank'); else toast('No meeting URL', 'info'); }}
                        style={{ padding: '8px 10px', borderRadius: '8px', border: '1.5px solid var(--border2)', background: 'white', fontFamily: 'inherit', fontSize: '11px', color: 'var(--text2)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                      >
                        <i className={`ti ${PLATFORM_CONFIG[meeting.platform]?.icon || 'ti-link'}`} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Invite Bot Modal */}
      {inviteTarget && (
        <InviteBotModal
          meeting={inviteTarget}
          onClose={() => setInviteTarget(null)}
        />
      )}

      {/* AI Recap Modal */}
      {recapTarget && meetingAiNotes[recapTarget.id] && (
        <AiRecapModal
          meeting={recapTarget}
          recap={meetingAiNotes[recapTarget.id]}
          onClose={() => setRecapTarget(null)}
        />
      )}

      <style>{`
        @keyframes spin   { to { transform: rotate(360deg); } }
        @keyframes pulse  { 0%,100% { opacity:1; } 50% { opacity:.4; } }
      `}</style>
    </div>
  );
}
